// src/components/ManageRSSFeeds.js
import React, { useState, useEffect } from 'react';
import db from '../firebase';
import { 
  TextField, Button, List, ListItem, ListItemText, IconButton, Grid,
  Collapse, Card, CardContent, Typography, CardActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axios from 'axios';
import { collection, addDoc, getDocs, deleteDoc, doc, where, query, getCountFromServer, updateDoc, orderBy, limit } from 'firebase/firestore';

const ManageRSSFeeds = () => {
  const [rssFeeds, setRssFeeds] = useState([]);
  const [newRssFeed, setNewRssFeed] = useState('');
  const [expandedFeed, setExpandedFeed] = useState(null);
  const [feedLogs, setFeedLogs] = useState({});
  const [currentLogIndex, setCurrentLogIndex] = useState({});

  // useEffect hook to fetch RSS feeds from Firestore when the component mounts
  useEffect(() => {
    const fetchFeeds = async () => {
      console.log('Fetching feeds');
      // Fetch all documents from the 'rssFeeds' collection
      const rssFeedCollection = await getDocs(collection(db, 'rssFeeds'));
      // Map the documents to an array of feed objects
      const rssFeedsData = rssFeedCollection.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Update the state with the fetched feeds
      setRssFeeds(rssFeedsData);
    };
    fetchFeeds();
  }, []);

  // Function to update the article counts for each RSS feed
  const handleUpdateArticleCounts = async () => {
    // Use Promise.all to wait for all feed updates to complete
    const updatedFeeds = await Promise.all(
      rssFeeds.map(async (feed) => {
        // Query the 'articles' collection for articles with the current feed's URL
        const articlesQuery = query(collection(db, 'articles'), where('feedURL', '==', feed.url));
        // Get the count of articles that match the query
        const snapshot = await getCountFromServer(articlesQuery);
        const articleCount = snapshot.data().count;
        // Update the 'articleCount' field in the 'rssFeeds' collection
        await updateDoc(doc(db, 'rssFeeds', feed.id), { articleCount });
        // Return the updated feed object
        return { ...feed, articleCount };
      })
    );
    // Update the state with the updated feeds
    setRssFeeds(updatedFeeds);
  };

  // Function to handle adding a new RSS feed
  const handleAddRssFeed = async () => {
    try {
      // Fetch the RSS feed data from the API
      const response = await axios.get(`https://api.rss2json.com/v1/api.json?rss_url=${newRssFeed}&api_key=rcp6suz4cga6o5rdefgwoeiwdm84c9lbm8i1apvs`);
      if (response.data.status === 'ok') {
        // Add the new feed to the 'rssFeeds' collection in Firestore
        const docRef = await addDoc(collection(db, 'rssFeeds'), { url: newRssFeed });
        // Update the state with the new feed
        setRssFeeds([...rssFeeds, { id: docRef.id, url: newRssFeed, articleCount: 0 }]);
        // Clear the input field
        setNewRssFeed('');
      } else {
        alert('Invalid RSS feed URL');
      }
    } catch (error) {
      alert('Invalid RSS feed URL');
    }
  };

  // Function to handle deleting an RSS feed
  const handleDeleteRssFeed = async (id) => {
    try {
      // Delete the feed document from the 'rssFeeds' collection in Firestore
      await deleteDoc(doc(db, 'rssFeeds', id));
      // Update the state to remove the deleted feed
      setRssFeeds(rssFeeds.filter(feed => feed.id !== id));
    } catch (error) {
      console.error('Error deleting RSS feed:', error);
    }
  };

  // Add function to fetch logs for a feed
  const fetchFeedLogs = async (feedId) => {
    try {
      const logsRef = collection(db, 'rssFeeds', feedId, 'logs');
      const q = query(logsRef, orderBy('timestamp', 'desc'), limit(10));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
    } catch (error) {
      console.error('Error fetching logs:', error);
      return [];
    }
  };

  // Handle feed expansion
  const handleFeedClick = async (feedId) => {
    if (expandedFeed === feedId) {
      setExpandedFeed(null);
      return;
    }
    
    setExpandedFeed(feedId);
    if (!feedLogs[feedId]) {
      const logs = await fetchFeedLogs(feedId);
      setFeedLogs(prev => ({ ...prev, [feedId]: logs }));
      setCurrentLogIndex(prev => ({ ...prev, [feedId]: 0 }));
    }
  };

  // Navigation handlers
  const handlePrevLog = (feedId) => {
    setCurrentLogIndex(prev => ({
      ...prev,
      [feedId]: Math.max(0, prev[feedId] - 1)
    }));
  };

  const handleNextLog = (feedId) => {
    setCurrentLogIndex(prev => ({
      ...prev,
      [feedId]: Math.min(feedLogs[feedId].length - 1, prev[feedId] + 1)
    }));
  };

  return (
    <div>
      <h2>Manage RSS Feeds</h2>
      <ListItem>
        <Grid container alignItems="center">
          <Grid item xs={8}>
            <TextField
              label="RSS Feed URL"
              value={newRssFeed}
              onChange={(e) => setNewRssFeed(e.target.value)}
            />
          </Grid>
          <Grid item xs={2}>
            <Button onClick={handleAddRssFeed} variant="contained" color="primary">Add</Button>
          </Grid>
          <Button onClick={handleUpdateArticleCounts} variant="contained" color="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>
            Update Article Counts
          </Button>
          <Grid item xs={2}>
          </Grid>
        </Grid>
      </ListItem>
      <List>
        {/* Headers */}
        <ListItem>
          <Grid container alignItems="center">
            <Grid item xs={8}>
              <ListItemText primary="RSS Feed URL" />
            </Grid>
            <Grid item xs={2}>
              <ListItemText primary="Article Count" />
            </Grid>
            <Grid item xs={2}>
              <ListItemText primary="Actions" />
            </Grid>
          </Grid>
        </ListItem>
        {/* Data Rows */}
        {rssFeeds.map(feed => (
          <React.Fragment key={feed.id}>
            <ListItem 
              button
              onClick={() => handleFeedClick(feed.id)}
              style={{ cursor: 'pointer' }}
            >
              <Grid container alignItems="center">
                <Grid item xs={7}>
                  <ListItemText primary={feed.url} />
                </Grid>
                <Grid item xs={2}>
                  <ListItemText primary={feed.articleCount} />
                </Grid>
                <Grid item xs={2}>
                  <IconButton edge="end" onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRssFeed(feed.id);
                  }}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={1}>
                  {expandedFeed === feed.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Grid>
              </Grid>
            </ListItem>
            
            <Collapse in={expandedFeed === feed.id}>
              <Card sx={{ m: 2 }}>
                {feedLogs[feed.id]?.length > 0 ? (
                  <>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        RSS Feed Log
                      </Typography>
                      {feedLogs[feed.id] && (
                        <div>
                          <Typography variant="body2" color="text.secondary">
                            Timestamp: {feedLogs[feed.id][currentLogIndex[feed.id]]?.timestamp?.toLocaleString()}
                          </Typography>
                          <Typography variant="body2">
                            Status: {feedLogs[feed.id][currentLogIndex[feed.id]]?.status}
                          </Typography>
                          <Typography variant="body2">
                            Items Found: {feedLogs[feed.id][currentLogIndex[feed.id]]?.itemCount}
                          </Typography>
                          {feedLogs[feed.id][currentLogIndex[feed.id]]?.error && (
                            <Typography color="error">
                              Error: {feedLogs[feed.id][currentLogIndex[feed.id]]?.error.message}
                            </Typography>
                          )}
                        </div>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={() => handlePrevLog(feed.id)}
                        disabled={currentLogIndex[feed.id] === 0}
                      >
                        Previous
                      </Button>
                      <Typography variant="body2" sx={{ mx: 2 }}>
                        {currentLogIndex[feed.id] + 1} of {feedLogs[feed.id].length}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<NavigateNextIcon />}
                        onClick={() => handleNextLog(feed.id)}
                        disabled={currentLogIndex[feed.id] === feedLogs[feed.id].length - 1}
                      >
                        Next
                      </Button>
                    </CardActions>
                  </>
                ) : (
                  <CardContent>
                    <Typography color="text.secondary">No logs available</Typography>
                  </CardContent>
                )}
              </Card>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    </div>
  );
};

export default ManageRSSFeeds;
